import React, {FC, useState} from "react"
import {isNull} from "lodash"
import {Col, Row} from "react-bootstrap"

import {TiersResponse} from "@irati/types"
import Form from "@irati/ui/src/Form"
import Card from "@irati/ui/src/Layout/Card"
import {Body, Label, H3} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import {twoDecimalPlaces} from "@irati/utils/validation/regex/number"
import Input from "@irati/ui/src/FormItem/Input"
import Flex from "@irati/ui/src/Flex"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {withTheme, WithDefaultTheme} from "styled-components"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Loading from "@irati/ui/src/Loading"
import mfaService from "@irati/auth/services/mfa"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Expander from "@irati/ui/src/Expander"

import History from "@irati/utils/history"
import PropTypes from "prop-types"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {TopUpPayload, TopUpOnChange, ServiceFeeValue} from "../../../containers/AdminReward/AdminRewardTopUp"
import Intro from "../TopUpRewardPoints/Intro"
import {StyledSelect, SaveMore} from "./styled"
import ServiceFeeOfferCard from "./ServiceFeeOfferCard"
import ServiceFeesCards from "./ServiceFeesCards"

interface Props extends WithDefaultTheme, InjectIntl {
  topupAmount: number
  totalToPay: number
  payload: TopUpPayload
  onPayloadChange: TopUpOnChange
  onTopupAmountChange: (topupAmount: number) => void
  load: Promise<void>
  serviceFeeValue: ServiceFeeValue
  serviceFeesTiers: TiersResponse[]
}

const currencySchema = () => {
  return Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
    .matches(twoDecimalPlaces, "wallet.reward.admin.distribute.validation.decimal")
    .test("Positive", "wallet.reward.admin.distribute.validation.pointsPerUser.required", (val) => val > 0 || isNull(val))
}

const TopUp: FC<Props> = (
  {theme, payload, onPayloadChange, topupAmount, totalToPay, onTopupAmountChange, serviceFeeValue, serviceFeesTiers, load, formatMessage},
  context
) => {
  const [mfaLoad, setMfaLoad] = useState<Promise<any>>(mfaService.hasFlag())

  const handleAmount = (updatedTopupAmount: string) => {
    const newTopupAmount = parseFloat(updatedTopupAmount) || 0
    const rpPoints = Math.round(newTopupAmount * payload.rpExchangeRate)
    onTopupAmountChange(newTopupAmount * 100)
    onPayloadChange({...payload, rpPoints: rpPoints || 0})
  }
  const handleCurrency = (purchaseCurrency: string) => {
    onPayloadChange({...payload, purchaseCurrency})
  }

  const handleMFARetry = async () => {
    setMfaLoad(mfaService.hasFlag())
  }

  const hasServiceFeeDiscount = serviceFeeValue.tierServiceFeeAmount !== serviceFeeValue.baseServiceFeeAmount

  const currencyOptions = [{label: payload.purchaseCurrency, value: payload.purchaseCurrency}]

  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} />}>
      <Row>
        <Col md={12} lg={5}>
          <Intro />
        </Col>
        <Col md={12} lg={7}>
          <Loading load={load}>
            <Card>
              <ServiceFeeOfferCard purchaseCurrency={payload.purchaseCurrency} />
              <Form>
                <Flex direction="column">
                  <Row>
                    <Col md={7} sm={7} xs={12}>
                      <Flex direction="column" flex="1">
                        <Label large>
                          <FormattedMessage id="wallet.reward.admin.card.topup.amountDue" />
                        </Label>
                        <Flex>
                          <StyledSelect
                            name="currency"
                            isClearable={false}
                            placeholder={payload.purchaseCurrency}
                            options={currencyOptions}
                            value={payload.purchaseCurrency}
                            onChange={handleCurrency}
                          />
                          <Input
                            name="amount"
                            type="string"
                            placeholder="wallet.reward.admin.card.topup.placeholder"
                            validation={currencySchema()}
                            value={topupAmount / 100}
                            onChange={handleAmount}
                          />
                        </Flex>
                      </Flex>
                    </Col>
                    <Col md={5} sm={5} xs={12}>
                      <Flex direction="column">
                        <Label large>
                          <FormattedMessage id="wallet.reward.admin.card.topup.rewardPoints" />
                        </Label>
                        <Row>
                          <Col md={2} xs={2}>
                            <Icon icon={IconTypes.rewardPoints} size={35} marginTop={2} />
                          </Col>
                          <Col md={10} xs={10}>
                            <Flex
                              style={{backgroundColor: theme.colors.greys.lightest}}
                              alignItems="center"
                              flex="1"
                              marginTop={1}
                              marginBottom={2}
                              paddingLeft
                              height="50px">
                              {payload.rpPoints}
                            </Flex>
                          </Col>
                        </Row>
                      </Flex>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Flex>
                          <Body color={theme.colors.greys.black}>
                            <FormattedMessage id="wallet.reward.admin.card.topup.serviceFee" />
                          </Body>
                          <Tooltip
                            tooltipChildren={
                              <Body small>
                                <FormattedMessage id="wallet.reward.admin.card.topup.tooltipBody" />
                              </Body>
                            }>
                            <Icon icon={IconTypes.circleInformation} small marginLeft={1} />
                          </Tooltip>
                        </Flex>
                        <Flex>
                          <Body
                            textDecoration={hasServiceFeeDiscount ? "line-through" : ""}
                            color={theme.colors.greys.black}
                            marginRight={1.5}>
                            {formatCurrency({
                              currencyCode: payload.purchaseCurrency,
                              value: serviceFeeValue.baseServiceFeeAmount
                            })}
                          </Body>
                          {hasServiceFeeDiscount && (
                            <Body color={theme.colors.tertiary.g_600}>
                              {formatCurrency({
                                currencyCode: payload.purchaseCurrency,
                                value: serviceFeeValue.tierServiceFeeAmount
                              })}
                            </Body>
                          )}
                        </Flex>
                      </Flex>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Expander
                        HeaderLabel={SaveMore}
                        showText={formatMessage({id: "wallet.reward.admin.card.topup.serviceFeeExpanderText"})}
                        iconSize={12}>
                        <ServiceFeesCards purchaseCurrency={payload.purchaseCurrency} serviceFeesTiers={serviceFeesTiers} />
                      </Expander>
                    </Col>
                  </Row>

                  <Flex alignItems="center" justifyContent="space-between" marginTop={5} marginBottom={5}>
                    <H3>
                      <FormattedMessage id="wallet.reward.admin.card.topup.serviceFeeTotal" />
                    </H3>
                    <Body large>{formatCurrency({currencyCode: payload.purchaseCurrency, value: totalToPay})}</Body>
                  </Flex>
                  <ValidationButton
                    id="rewardWallet_topUp_continue_button"
                    onClick={() => History.push("/flexi-checkout/checkout", context)}
                    primary>
                    <FormattedMessage id="platform.button.continue" />
                  </ValidationButton>
                </Flex>
              </Form>
            </Card>
          </Loading>
        </Col>
      </Row>
    </Loading>
  )
}

TopUp.contextTypes = {
  appPath: PropTypes.string
}

export default withTheme(injectIntl(TopUp))
